<template>
  <div class="centered-card">
    <el-card class="authorize-card" style="margin-top: 1.5rem">
      <!-- Manual / Help Icon -->
      <div style="position: absolute; right: 0; z-index: 10000">
        <ButtonManual
          v-if="applicationData.type"
          :application-name="applicationData.software_name"
          :application-type="applicationData.type" />
      </div>

      <div
        class="align-items-center my-6 flex items-center justify-center gap-6">
        <LogoIcon class="h-16 text-primary dark:text-white" />
        <ArrowLeftRight class="text-contrast-muted" />
        <ApplicationIcon :application-name="applicationName" :size="60" />
      </div>
      <h1 class="mb-3 text-center">
        {{
          i18n.t('views.authorize.title', {
            softwareName: applicationName,
          })
        }}
      </h1>
      <p class="mb-8 text-center">
        {{ i18n.t('views.authorize.subTitle') }}
      </p>

      <SmDialogMessage
        v-if="errorMessageContent"
        :visible="errorMessageContent.visible"
        :message="errorMessageContent.message"
        :type="errorMessageContent.type"
        class="mb-4" />

      <!-- Custom Login Flow -->
      <ApplicationAuthFlowContent
        v-if="route.query.customAuthorizationFlow"
        :application-type="applicationType"
        :auth-type="AuthType.AUTHENTICATE"
        :application-id="applicationId"
        :application-name="applicationName"
        @authentication-completed="successCallback" />

      <!-- Individual integration Component -->
      <component
        :is="page"
        v-else-if="page"
        v-model:software-data="applicationData"
        v-model:data-source="dataSource"
        v-model:form-ref="formRef" />

      <!-- Dynamic Form based on credential_scheme -->
      <DynamicIntegrationForm
        v-else-if="applicationType !== SoftwareTypes.OAUTH"
        v-model:form-ref="formRef"
        :application-name="applicationData.software_name"
        :type="applicationType" />

      <SmButton
        v-if="!route.query.customAuthorizationFlow"
        type="primary"
        class="mx-auto block"
        plain
        :loading="isConnectLoading"
        @click="handleConnectButtonClick">
        {{ i18n.t('general.connect') }}
      </SmButton>
    </el-card>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'

  import LogoIcon from '@/assets/img/LogoIcon.vue'
  import type { ApiError, SoftwarePostIn } from '@/client'
  import {
    DataSourceAuthorizationService,
    OpenAPI,
    SoftwareAuthorizationService,
    SoftwareTypes,
  } from '@/client'
  import { sendToast } from '@/components/sm/SmNotification'
  import validate from '@/utils/validate'
  import { useGlobalStore } from '@/stores/globalStore'
  import { AuthType } from '@/stores/applicationReauthStore'
  import { DataSourceCredentialRequest } from '@/types/dataSource'
  import {
    SmDialogMessageProps,
    DialogMessageTypes,
  } from '@/components/sm/SmDialogMessage.vue'
  import { capitalize } from '@/utils/formatter/formatter'
  import { ArrowLeftRight } from 'lucide-vue-next'

  type ExtendedApplication = SoftwarePostIn & { isValid: boolean }
  const i18n = useI18n()
  const route = useRoute()
  const store = useGlobalStore()

  const formRef = ref()

  const emit = defineEmits<{
    errorMessage: [message: SmDialogMessageProps]
  }>()

  // ---------------------------------------------------------------------------
  // Load Data
  // ---------------------------------------------------------------------------

  const accessToken = ref(route.query.accessToken) as Ref<string>
  const applicationType = ref(route.query.softwareType) as Ref<SoftwareTypes>
  const applicationName = ref(route.query.softwareName) as Ref<string>
  const applicationId = ref(route.query.softwareId) as Ref<string | undefined>

  const reauthenticate = ref(route.query.reauthenticate) as Ref<string>

  const applicationData = ref<ExtendedApplication>({
    software_name: applicationName.value,
    type: applicationType.value,
    isValid: false,
  })

  const errorMessageContent = ref<SmDialogMessageProps>({
    message: 'content',
    visible: false,
    type: 'info',
  })

  OpenAPI.TOKEN = accessToken.value

  const dataSource = ref<DataSourceCredentialRequest>({})

  // ---------------------------------------------------------------------------
  // Load individual components
  // ---------------------------------------------------------------------------

  const pages = import.meta.glob('@/components/integrations/modules/*.vue')

  const page = ref()

  watch(
    () => [applicationData.value.software_name, applicationData.value.type],
    async ([softwareName, softwareType]) => {
      if (!softwareName || !softwareType) return

      const newFilename = `${capitalize(softwareType)}${softwareName}`
      const matchPath = `/src/components/integrations/modules/${newFilename}.vue`

      if (pages[matchPath]) {
        page.value = defineAsyncComponent(
          pages[matchPath] as () => Promise<{ default: Component }>
        )
      } else {
        console.warn('Component not found:', matchPath)
        page.value = null
      }
    },
    { immediate: true }
  )

  // ---------------------------------------------------------------------------
  // Handle Connect
  // ---------------------------------------------------------------------------

  const isConnectLoading = ref<boolean>(false)

  // OAuth Link
  function getOAuthLink(applicationName: string) {
    SoftwareAuthorizationService.getApiV1OauthOauthLinkGet({
      softwareName: applicationName,
      readOnly: route.query.readonly === 'true',
    }).then((result) => {
      window.location.href = result
    })
  }

  async function handleConnectButtonClick() {
    if (isConnectLoading.value) {
      return
    }

    if (applicationType.value === SoftwareTypes.OAUTH) {
      isConnectLoading.value = true
      getOAuthLink(applicationName.value)
      return
    }

    if (!formRef.value) return
    const isUseForm = Object.keys(formRef.value).includes('errors')

    const valid = isUseForm
      ? formRef.value.validate()
      : await validate(formRef.value)

    if (!valid) return

    if (reauthenticate.value === 'true' && !applicationId.value) {
      errorMessageContent.value = {
        message: i18n.t('error.general.unknown_error.message') as string,
        type: 'error',
        visible: true,
      }
      return
    }

    isConnectLoading.value = true

    if (isUseForm) {
      const raw = formRef.value.fields
      applicationData.value.credentials = Object.fromEntries(
        Object.entries(raw).map(([key, value]) => [
          key,
          Array.isArray(value) ? value[0] : value,
        ])
      )
    }

    let promise = null

    const hasCredentials = !!Object.keys(
      applicationData.value.credentials || {}
    ).length

    // Create Promise based on the type of software
    if (
      reauthenticate.value == 'true' &&
      hasCredentials &&
      applicationId.value
    ) {
      // Reauth
      promise =
        SoftwareAuthorizationService.reauthorizeSoftwareApiV1SoftwareSoftwareIdReauthorizePost(
          {
            softwareId: applicationId.value,
            requestBody: applicationData.value.credentials || {},
          }
        )
    } else if (hasCredentials) {
      // integration
      promise =
        SoftwareAuthorizationService.postSoftwareApiV1SoftwareSoftwarePost({
          requestBody: {
            ...applicationData.value,
            read_only: route.query.readonly === 'true',
          },
        })
    } else {
      // datasource
      promise =
        DataSourceAuthorizationService.postDataSourceApiV1DataSourceDataSourcesPost(
          {
            requestBody: {
              name: applicationName.value,
              integration_id: applicationName.value,
              identifier: route.query.identifier as string,
              credentials: dataSource.value,
            },
          }
        )
    }

    // Resolve the promise and handle success or error
    promise
      .then(() => {
        successCallback()
      })
      .catch((error: ApiError) => {
        handleErrorMessage(error)
      })
      .finally(() => {
        isConnectLoading.value = false
      })
  }

  document.addEventListener('keypress', (event) => {
    if (event.key === 'Enter' && !store.state.showAuthFlowDialog) {
      handleConnectButtonClick()
    }
  })

  function handleErrorMessage(error: ApiError) {
    let title: string | null = null
    let message: string | null = null
    let type: DialogMessageTypes = 'info'

    if (
      i18n.te(`${error.body.detail.error}.title`) &&
      i18n.te(`${error.body.detail.error}.message`)
    ) {
      title = i18n.t(`${error.body.detail.error}.title`)
      message = i18n.t(`${error.body.detail.error}.message`)
    } else {
      title = i18n.t('error.general.unknown_error.title')
      message = i18n.t('error.general.unknown_error.message')
    }

    switch (error.body.detail.error) {
      case 'error.integration.bot.waiting_for_authorization':
        type = 'success'
        title = i18n.t('authPending.title')
        message = i18n.t('authPending.description')
        break

      default:
        type = 'error'
    }

    title = `${title}: \n${message}`

    errorMessageContent.value = {
      message: title,
      type: type,
      visible: title !== null && type !== null,
    }

    emit('errorMessage', errorMessageContent.value)
  }

  function successCallback() {
    sendToast(
      i18n.t('notifications.authorize.connectSuccess.title'),
      i18n.t('notifications.authorize.connectSuccess.message'),
      'success'
    )

    setTimeout(() => {
      window.close()
    }, 1500)
  }
</script>

<style scoped>
  .centered-card {
    position: fixed;
    top: 45%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .row {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-content: center;
  }

  .integration-icon {
    height: 5rem;
  }

  .x {
    font-size: 2.5rem;
    margin: 0;
    vertical-align: middle;
  }

  .x-wrapper {
    margin-left: 1rem;
    margin-right: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--el-text-color-disabled);
  }
</style>
